'use client'

import React from 'react'
import Link from 'next/link'
import { Globe } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PlatformIcon } from '@/components/landing/platform-icon'
import { Reveal } from '@/components/site/reveal'
import { SiteSection } from '@/components/site/section'
import { trackSiteLinkClicked } from '@/lib/analytics'

const SERVICES = [
  { key: 'dictation', number: '01', href: '/dictation' },
  { key: 'transcription', number: '02', href: '/transcription' },
  { key: 'meetingNotes', number: '03', href: '/meeting-notes' },
] as const

type Service = (typeof SERVICES)[number]['key']

/** 同一張整卡連結內的裝飾片段，不含假的可操作控制項。 */
function ServiceSample({ service }: { service: Service }) {
  const { t } = useTranslation(['web'])
  const text = (key: string) => t(`web:homePage.services.${service}.${key}`)
  return (
    <div aria-hidden="true" className="mt-(--gap-md) text-sm">
      <div className="border-b border-line-soft pb-[9px] font-mono text-2xs tracking-[0.06em] text-muted">
        {text('sampleLabel')}
      </div>
      <div className="pt-3.5">
        {service === 'dictation' ? (
          <>
            <div className="min-h-[62px] rounded-sm border border-border px-3 py-2.5 leading-[1.6] text-foreground">
              {text('sampleText')}
              <span className="ml-px inline-block h-[1em] w-0.5 bg-foreground align-[-2px]" />
            </div>
            <div className="mt-2.5 flex items-center gap-2 text-muted">
              <span className="inline-flex h-[22px] min-w-[26px] items-center justify-center rounded-[5px] border border-b-2 border-border px-1.5 font-mono text-2xs text-foreground">
                {text('key')}
              </span>
              <span>{text('hint')}</span>
            </div>
          </>
        ) : service === 'transcription' ? (
          <>
            {(['A', 'B'] as const).map((speaker) => (
              <div
                key={speaker}
                className="grid grid-cols-[22px_1fr] items-start gap-2.5 border-t border-line-soft py-2 first:border-0 first:pt-0"
              >
                <span className="grid size-[22px] place-items-center rounded-full bg-foreground font-mono text-3xs text-background">
                  {text(`speaker${speaker}`)}
                </span>
                <span>
                  <span className="mr-2 font-mono text-2xs text-muted">
                    {text(`time${speaker}`)}
                  </span>
                  {text(`text${speaker}`)}
                </span>
              </div>
            ))}
          </>
        ) : (
          <>
            <p className="mb-1.5 font-mono text-2xs tracking-[0.12em] text-muted uppercase">
              {text('todo')}
            </p>
            {(['A', 'B'] as const).map((task) => (
              <div
                key={task}
                className="grid grid-cols-[14px_1fr_auto] items-baseline gap-2 border-t border-line-soft py-1.5"
              >
                <span
                  className={`relative top-px size-3 rounded-[3px] border ${task === 'A' ? 'border-ok bg-ok' : 'border-foreground'}`}
                />
                <span>{text(`task${task}`)}</span>
                <span className="font-mono text-2xs text-muted">{text(`owner${task}`)}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export function HomeServicesSection() {
  const { t } = useTranslation(['web'])
  return (
    <SiteSection id="routes" trackingKey="services" className="scroll-mt-[84px]">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.28em] text-muted uppercase">
          {t('web:homePage.services.eyebrow')}
        </p>
      </Reveal>
      <Reveal className="mt-(--gap-lg) grid grid-cols-3 gap-x-(--gap-lg) border-t border-foreground max-[920px]:grid-cols-1">
        {SERVICES.map(({ key, number, href }) => (
          <Link
            key={key}
            href={href}
            onClick={() => trackSiteLinkClicked('services', href)}
            className="group relative flex min-w-0 flex-col pt-[26px] pb-[30px] text-foreground before:absolute before:top-[26px] before:bottom-[30px] before:left-[calc(var(--gap-lg)/-2)] before:border-l before:border-line-soft first:before:hidden after:absolute after:inset-x-0 after:-top-px after:h-0.5 after:origin-left after:scale-x-0 after:bg-brand after:transition-transform after:duration-220 hover:after:scale-x-100 focus-visible:rounded-[2px] focus-visible:outline-2 focus-visible:outline-offset-[10px] focus-visible:outline-foreground focus-visible:after:scale-x-100 max-[920px]:border-t max-[920px]:border-line-soft max-[920px]:first:border-t-0 max-[920px]:before:hidden"
          >
            <span
              aria-hidden="true"
              className="font-display text-(length:--fs-index-xl) leading-none font-medium text-(--num-faint)"
            >
              {number}
            </span>
            <p className="mt-[18px] text-md leading-[1.6] text-muted">
              {t(`web:homePage.services.${key}.when`)}
            </p>
            <h3
              data-display-font=""
              className="mt-3.5 font-display text-[length:clamp(27px,2.9vw,36px)] leading-[1.24] font-semibold"
            >
              {t(`web:homePage.services.${key}.name`)}
            </h3>
            <p className="mt-[18px] text-md leading-[1.7] text-muted">
              {t(`web:homePage.services.${key}.description`)}
            </p>
            <ServiceSample service={key} />
            <p className="mt-auto flex flex-wrap items-center gap-x-3.5 gap-y-1.5 pt-5">
              <span className="font-mono text-2xs tracking-[0.16em] text-muted uppercase">
                {t('web:homePage.services.platformLabel')}
              </span>
              <span
                role="img"
                aria-label={t(
                  key === 'dictation'
                    ? 'web:homePage.services.appPlatforms'
                    : 'web:homePage.services.browserLabel'
                )}
                className="inline-flex items-center gap-3 text-foreground"
              >
                {key === 'dictation' ? (
                  <>
                    <PlatformIcon platform="macos" size={17} />
                    <PlatformIcon platform="windows" size={17} />
                    <PlatformIcon platform="android" size={17} />
                  </>
                ) : (
                  <>
                    <Globe size={17} strokeWidth={1.7} aria-hidden="true" />
                    <span className="text-sm text-muted">
                      {t('web:homePage.services.browserText')}
                    </span>
                  </>
                )}
              </span>
            </p>
            <span className="self-start border-b border-brand/34 pt-[18px] text-md font-normal text-brand transition-colors group-hover:border-brand-hover group-hover:text-brand-hover group-focus-visible:border-brand-hover group-focus-visible:text-brand-hover">
              {t(`web:homePage.services.${key}.cta`)}
            </span>
          </Link>
        ))}
      </Reveal>
    </SiteSection>
  )
}
